import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { MemberListItem, OrganizationSettingsResponse, ResetResponsesResult } from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';

/** „Abstimmungen zurücksetzen" (§ Nutzeranfrage) — löscht Saisonspiel-
 * Rückmeldungen, Trainings-Anmeldungen und Verlegungsabstimmungs-Stimmen,
 * entweder für ein einzelnes Mitglied oder für den ganzen Verein. Kein
 * eigener Bestätigungsdialog-Baustein in dieser Codebasis — `confirm()`,
 * wie bei jeder anderen destruktiven Admin-Aktion (z. B. Mitglied löschen). */
function ResetResponsesCard({ readOnly }: { readOnly: boolean }) {
  const [memberId, setMemberId] = useState('');
  const [result, setResult] = useState<ResetResponsesResult | null>(null);

  const { data: members } = useQuery({
    queryKey: ['org', 'user-management', 'members-for-reset'],
    queryFn: async () => {
      const page = await api<{ data: MemberListItem[] }>('/members?pageSize=200', { orgScoped: true });
      return [...page.data].sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      api<ResetResponsesResult>('/org/reset-responses', {
        method: 'POST',
        orgScoped: true,
        body: { memberId: memberId || undefined },
      }),
    onSuccess: (r) => setResult(r),
  });

  const selectedMember = members?.find((m) => m.id === memberId);
  const scopeLabel = selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : 'alle Mitglieder';

  return (
    <Card title="Abstimmungen zurücksetzen">
      <p className="mb-3 text-xs text-slate-400">
        Löscht Saisonspiel-Rückmeldungen, Trainings-Anmeldungen und Verlegungsabstimmungs-Stimmen — für ein
        einzelnes Mitglied oder für den ganzen Verein. Betroffene können danach erneut abstimmen.
        Nicht rückgängig zu machen.
      </p>
      {result && (
        <Alert kind="success">
          Zurückgesetzt: {result.seasonResponses} Saisonspiel-Rückmeldung(en), {result.trainingRegistrations}{' '}
          Trainings-Anmeldung(en), {result.rescheduleVotes} Verlegungs-Stimme(n).
        </Alert>
      )}
      {reset.isError && <Alert>Zurücksetzen fehlgeschlagen.</Alert>}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Mitglied (leer = alle)">
          <select
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
            value={memberId}
            disabled={readOnly}
            onChange={(e) => {
              setMemberId(e.target.value);
              setResult(null);
            }}
          >
            <option value="">Alle Mitglieder</option>
            {members?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
        </Field>
        {!readOnly && (
          <Button
            type="button"
            variant="danger"
            disabled={reset.isPending}
            onClick={() => {
              if (
                confirm(
                  `Wirklich alle Abstimmungen für ${scopeLabel} zurücksetzen? Das kann nicht rückgängig gemacht werden.`,
                )
              ) {
                reset.mutate();
              }
            }}
          >
            {reset.isPending ? 'Wird zurückgesetzt …' : 'Zurücksetzen'}
          </Button>
        )}
      </div>
    </Card>
  );
}

export function OrgUserManagementPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');

  const { data } = useQuery({
    queryKey: ['org', 'settings'],
    queryFn: () => api<OrganizationSettingsResponse>('/org/settings', { orgScoped: true }),
  });

  const [validityDays, setValidityDays] = useState('');
  const [validitySaved, setValiditySaved] = useState(false);
  useEffect(() => {
    if (data) setValidityDays(String(data.inviteValidityDays));
  }, [data]);
  const saveValidity = useMutation({
    mutationFn: (days: number) =>
      api<OrganizationSettingsResponse>('/org/settings', {
        method: 'PATCH',
        orgScoped: true,
        body: { inviteValidityDays: days },
      }),
    onSuccess: () => {
      setValiditySaved(true);
      void qc.invalidateQueries({ queryKey: ['org', 'settings'] });
      setTimeout(() => setValiditySaved(false), 2000);
    },
  });

  const [inactiveMonths, setInactiveMonths] = useState('');
  const [inactiveSaved, setInactiveSaved] = useState(false);
  useEffect(() => {
    if (data) setInactiveMonths(data.inactiveUserDeleteAfterMonths != null ? String(data.inactiveUserDeleteAfterMonths) : '');
  }, [data]);
  const saveInactiveMonths = useMutation({
    mutationFn: (months: number | null) =>
      api<OrganizationSettingsResponse>('/org/settings', {
        method: 'PATCH',
        orgScoped: true,
        body: { inactiveUserDeleteAfterMonths: months },
      }),
    onSuccess: () => {
      setInactiveSaved(true);
      void qc.invalidateQueries({ queryKey: ['org', 'settings'] });
      setTimeout(() => setInactiveSaved(false), 2000);
    },
  });

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Einladungslink">
        {validitySaved && <Alert kind="success">Gespeichert.</Alert>}
        <p className="mb-3 text-xs text-slate-400">
          Wie viele Tage ein per „Mitglied/Benutzer einladen" (Mitgliederliste) erzeugter Link
          gültig bleibt, bevor er abläuft.
        </p>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(validityDays);
            if (Number.isInteger(n) && n >= 1 && n <= 365) saveValidity.mutate(n);
          }}
        >
          <Field label="Gültigkeit (Tage)">
            <Input
              type="number"
              min={1}
              max={365}
              value={validityDays}
              disabled={readOnly}
              onChange={(e) => setValidityDays(e.target.value)}
              className="w-28"
            />
          </Field>
          {!readOnly && (
            <Button type="submit" disabled={saveValidity.isPending}>
              Speichern
            </Button>
          )}
        </form>
      </Card>

      <Card title="Erweiterte Einstellungen">
        {inactiveSaved && <Alert kind="success">Gespeichert.</Alert>}
        <p className="mb-3 text-xs text-slate-400">
          Benutzerkonten, die seit so vielen Monaten nicht mehr angemeldet waren, werden
          automatisch vollständig gelöscht — Mitgliedsdatensatz und Zugang, nicht nur
          deaktiviert, nicht rückgängig zu machen. Leer lassen oder auf 0 setzen, um die
          automatische Löschung zu deaktivieren. Der zuletzt verbliebene Vereinsadministrator
          wird dabei nie gelöscht, auch nicht bei Inaktivität.
        </p>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = inactiveMonths.trim();
            if (trimmed === '' || trimmed === '0') {
              saveInactiveMonths.mutate(null);
              return;
            }
            const n = Number(trimmed);
            if (Number.isInteger(n) && n >= 1 && n <= 60) saveInactiveMonths.mutate(n);
          }}
        >
          <Field label="Inaktivität bis zur Löschung (Monate)">
            <Input
              type="number"
              min={0}
              max={60}
              value={inactiveMonths}
              disabled={readOnly}
              onChange={(e) => setInactiveMonths(e.target.value)}
              className="w-28"
            />
          </Field>
          {!readOnly && (
            <Button type="submit" disabled={saveInactiveMonths.isPending}>
              Speichern
            </Button>
          )}
        </form>
      </Card>

      <ResetResponsesCard readOnly={readOnly} />
    </div>
  );
}
